import { api } from './api';

export interface RoleAssignmentRow {
  id: string;
  role: string;
  scope_type: string;
  scope_id: string | null;
  school_name: string | null;
}

export interface AdminUserRow {
  id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  roles: RoleAssignmentRow[];
}

export interface SchoolRow {
  id: string;
  name: string;
  created_at: string;
  director_email?: string | null;
  teacher_count?: number;
}

export interface SchoolDirectorRow {
  id: string;
  user_id: string;
  email: string | null;
  suspended_at: string | null;
}

export interface SchoolDetailRow {
  id: string;
  name: string;
  department: string | null;
  created_at: string;
  director_email: string | null;
  teacher_count: number;
  class_count: number;
  student_count: number;
  active_student_count: number;
}

export interface AdminSchoolTeacherRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  active: boolean;
  created_at: string;
}

export interface SchoolClassroomRow {
  id: string;
  name: string;
  level: string | null;
  primary_teacher_name: string | null;
  active_count: number;
  total_count: number;
  archived: boolean;
}

export interface SchoolStudentRow {
  id: string;
  anon_id: string;
  active: boolean;
  classroom_name: string;
}

export async function fetchAdminUsers(opts?: {
  role?: string;
  schoolId?: string;
  page?: number;
  perPage?: number;
}): Promise<AdminUserRow[]> {
  return (await api.admin.users.list.query(opts ?? {})) as AdminUserRow[];
}

export async function fetchAdminSchools(): Promise<SchoolRow[]> {
  return (await api.admin.schools.list.query()) as SchoolRow[];
}

export async function createSchool(
  name: string,
  directorEmail: string | null,
  department?: string | null,
): Promise<{ school: SchoolRow; directorStatus: 'assigned' | 'invited' | 'none' }> {
  return (await api.admin.schools.create.mutate({ name, directorEmail, department })) as {
    school: SchoolRow;
    directorStatus: 'assigned' | 'invited' | 'none';
  };
}

export async function updateSchoolDepartment(schoolId: string, department: string | null): Promise<void> {
  await api.admin.schools.updateDepartment.mutate({ schoolId, department });
}

export async function assignRole(
  email: string,
  role: string,
  scopeType: 'platform' | 'school' | 'user',
  scopeId?: string,
): Promise<{ status: 'assigned' | 'already' }> {
  return (await api.admin.users.assignRole.mutate({ email, role, scopeType, scopeId })) as {
    status: 'assigned' | 'already';
  };
}

export async function removeRole(id: string): Promise<void> {
  await api.admin.users.removeRole.mutate({ id });
}

export async function suspendRole(id: string): Promise<void> {
  await api.admin.users.suspendRole.mutate({ id });
}

export async function reactivateRole(id: string): Promise<void> {
  await api.admin.users.reactivateRole.mutate({ id });
}

export async function inviteUser(
  email: string,
  role: string,
  scopeType: 'platform' | 'school' | 'user',
  scopeId?: string,
): Promise<{ status: 'invited' | 'assigned' }> {
  return (await api.admin.users.invite.mutate({ email, role, scopeType, scopeId })) as {
    status: 'invited' | 'assigned';
  };
}

export async function resendDirectorInvite(schoolId: string, userId: string, email: string): Promise<'invited'> {
  return (await api.admin.schools.resendDirectorInvite.mutate({ schoolId, userId, email })) as 'invited';
}

export async function fetchAdminSchoolsPaginated(
  page: number,
  limit: number,
): Promise<{ schools: SchoolDetailRow[]; total: number }> {
  return (await api.admin.schools.listPaginated.query({ page, limit })) as {
    schools: SchoolDetailRow[];
    total: number;
  };
}

export async function fetchAdminSchoolDetail(schoolId: string): Promise<SchoolDetailRow | null> {
  return (await api.admin.schools.get.query({ schoolId })) as SchoolDetailRow | null;
}

export async function fetchAdminSchoolDirectors(schoolId: string): Promise<SchoolDirectorRow[]> {
  return (await api.admin.schools.directors.query({ schoolId })) as SchoolDirectorRow[];
}

export async function fetchAdminSchoolTeachers(
  schoolId: string,
  page: number,
  limit: number,
): Promise<{ teachers: AdminSchoolTeacherRow[]; total: number }> {
  return (await api.admin.schools.teachers.query({ schoolId, page, limit })) as {
    teachers: AdminSchoolTeacherRow[];
    total: number;
  };
}

export async function fetchAdminSchoolClassrooms(
  schoolId: string,
  page: number,
  limit: number,
  includeArchived?: boolean,
): Promise<{ classrooms: SchoolClassroomRow[]; total: number }> {
  return (await api.admin.schools.classrooms.query({ schoolId, page, limit, includeArchived })) as {
    classrooms: SchoolClassroomRow[];
    total: number;
  };
}

export async function unarchiveClassroom(classroomId: string): Promise<void> {
  await api.admin.schools.unarchiveClassroom.mutate({ classroomId });
}

export async function fetchAdminSchoolStudents(
  schoolId: string,
  page: number,
  limit: number,
): Promise<{ students: SchoolStudentRow[]; total: number }> {
  return (await api.admin.schools.students.query({ schoolId, page, limit })) as {
    students: SchoolStudentRow[];
    total: number;
  };
}
